// src/search/useAddressSearch.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { makeClientRequestId, useLocationStore } from "@/location/locationStore";
import { useOnlineStatus } from "@/offline/useOnlineStatus";
import { offlineAddressSearchResponse } from "./offlineSearchResponse";
import type { AddressPage, AddressResult, SearchFilters } from "./types";
import { PAGE_SIZE } from "./constants";

interface UseAddressSearchOptions {
  filters: SearchFilters;
  hasParams: boolean;
}

export interface UseAddressSearchReturn {
  // Query state
  data: AddressPage | null;
  allResults: AddressResult[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isFetched: boolean;
  hasNextPage: boolean;

  // Pagination
  fetchNextPage: () => void;
  loadMoreRef: React.RefObject<HTMLDivElement | null>;

  // Location flow
  searchRequestId: string | null;
  beginSearchLocationFlow: () => string;
  clearSearchLocationFlow: () => void;

  // Helpers
  primeAddressDetail: (addr: AddressResult) => void;
  online: boolean;
}

export function useAddressSearch({
  filters,
  hasParams,
}: UseAddressSearchOptions): UseAddressSearchReturn {
  const online = useOnlineStatus();
  const queryClient = useQueryClient();

  // Location tracking state
  const requestLocation = useLocationStore((s) => s.requestLocation);
  const sendLocationPing = useLocationStore((s) => s.sendLocationPing);
  const [searchRequestId, setSearchRequestId] = useState<string | null>(null);
  const searchLocationRequestRef = useRef<string | null>(null);
  const pendingLocationRef = useRef<{
    id: string;
    location: ReturnType<typeof useLocationStore.getState>["lastLocation"];
    sent: boolean;
  } | null>(null);
  const searchCompletedRef = useRef(false);

  const maybeSendSearchLocation = useCallback(() => {
    const current = pendingLocationRef.current;
    if (!current || current.sent) return;
    if (!current.location) return;
    if (!online) return;
    if (!searchCompletedRef.current) return;
    if (!searchRequestId || searchRequestId !== current.id) return;

    current.sent = true;
    void sendLocationPing({
      context: "search",
      requestId: current.id,
      location: current.location,
      endpointOverride: `search-log/${current.id}/location/`,
      method: "patch",
    }).catch((err) => {
      console.debug("search location ping failed", err);
    });
  }, [online, searchRequestId, sendLocationPing]);

  const beginSearchLocationFlow = useCallback(() => {
    const id = makeClientRequestId();
    setSearchRequestId(id);
    searchLocationRequestRef.current = id;
    searchCompletedRef.current = false;
    pendingLocationRef.current = { id, location: null, sent: false };

    if (online) {
      requestLocation("search").then((res) => {
        if (searchLocationRequestRef.current !== id) return;
        if (res.state === "granted") {
          if (pendingLocationRef.current && pendingLocationRef.current.id === id) {
            pendingLocationRef.current.location = res.location;
          }
          maybeSendSearchLocation();
        }
      });
    }

    return id;
  }, [maybeSendSearchLocation, online, requestLocation]);

  const clearSearchLocationFlow = useCallback(() => {
    setSearchRequestId(null);
    searchLocationRequestRef.current = null;
    pendingLocationRef.current = null;
    searchCompletedRef.current = false;
  }, []);

  // Destructure filters for query key
  const { street, postcode, statusList, postcodeFacet, cityFacet, localityFacet, statusFacet } =
    filters;

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetched,
  } = useInfiniteQuery<AddressPage, Error>({
    queryKey: [
      "addresses",
      street,
      postcode,
      postcodeFacet,
      cityFacet,
      localityFacet,
      statusFacet,
      statusList.join(","),
      searchRequestId,
    ],
    enabled: hasParams && !!searchRequestId,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    staleTime: 0,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      const page = typeof pageParam === "number" ? pageParam : 1;
      const requestId = searchRequestId;

      if (!online) {
        return await offlineAddressSearchResponse({
          street,
          postcode,
          postcode_facet: postcodeFacet,
          post_town_facet: cityFacet,
          dependent_locality_facet: localityFacet,
          status_facet: statusFacet,
          status: statusList,
          page,
          page_size: PAGE_SIZE,
        });
      }

      const res = await api.get("address/", {
        params: {
          thoroughfare: street || undefined,
          postcode: postcode || undefined,
          postcode_facet: postcodeFacet.length ? postcodeFacet.join(",") : undefined,
          post_town_facet: cityFacet.length ? cityFacet.join(",") : undefined,
          dependent_locality_facet: localityFacet.length ? localityFacet.join(",") : undefined,
          status_facet: statusFacet.length ? statusFacet.join(",") : undefined,
          status: statusList.length ? statusList.join(",") : undefined,
          page,
          page_size: PAGE_SIZE,
          client_request_id: requestId ?? undefined,
        },
      });
      return {
        ...res.data,
        offline: false,
      };
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage?.next ? pages.length + 1 : undefined,
  });

  // Send location after search completes
  useEffect(() => {
    if (isFetched && searchRequestId) {
      searchCompletedRef.current = true;
      maybeSendSearchLocation();
    }
  }, [isFetched, searchRequestId, maybeSendSearchLocation]);

  // Flatten results from all pages
  const allResults =
    data?.pages?.flatMap((page) => page?.results ?? []) ?? [];

  // First page for facet data
  const firstPage = data?.pages?.[0] ?? null;

  // Infinite scroll ref
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || !hasParams) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: "300px" }
    );

    const node = loadMoreRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, hasParams]);

  /**
   * Pre-populate the address detail cache with search result data
   * so the drawer can render instantly.
   */
  const primeAddressDetail = useCallback(
    (addr: AddressResult) => {
      if (!addr?.udprn) return;

      const key = ["address", String(addr.udprn)];
      queryClient.setQueryData(key, (prev: AddressResult | undefined) => ({
        ...prev,
        ...addr,
        customer: addr.customer ?? prev?.customer ?? null,
        latest_note: addr.latest_note ?? prev?.latest_note ?? null,
        notes: (prev as { notes?: unknown[] })?.notes ?? (addr.latest_note ? [addr.latest_note] : []),
        offline: addr.offline ?? prev?.offline ?? false,
      }));
    },
    [queryClient]
  );

  return {
    data: firstPage,
    allResults,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetched,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    loadMoreRef,
    searchRequestId,
    beginSearchLocationFlow,
    clearSearchLocationFlow,
    primeAddressDetail,
    online,
  };
}
